/**
 * Schema.org JSON-LD builders for the public reports surface.
 *
 * These replaced next-seo's `<CollectionPageJsonLd>` / `<FAQPageJsonLd>`
 * components when /reports moved to the App Router (next-seo renders through
 * next/head, which is pages-router-only). The objects built here are
 * payload-equivalent to next-seo's output — same `@context`/`@type` framing,
 * same field order — and are rendered as inline body scripts via
 * `serializeJsonLd` (body placement is valid for Google structured data).
 */

export interface SchemaFaqItem {
  question: string
  answer: string
}

export interface SchemaCollectionPagePart {
  author: string
  about: string
  name: string
  datePublished: string
  image?: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function cleanString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function readAcceptedAnswerText(value: unknown): string | null {
  const answer = asRecord(Array.isArray(value) ? value[0] : value)
  return cleanString(answer?.text)
}

/** Drops items with blank questions or answers and trims the survivors. */
export function sanitizeSchemaFaqItems(
  items: Array<{ question: string; answer: string }>
): SchemaFaqItem[] {
  return items.flatMap((item) => {
    const question = cleanString(item.question)
    const answer = cleanString(item.answer)
    if (!question || !answer) return []
    return [{ question, answer }]
  })
}

/**
 * Extracts question/answer pairs from a stored schema.org FAQPage object
 * (`seo.jsonLd.faqPage`). Tolerates `acceptedAnswer` being a single object or
 * an array, and skips malformed entries.
 */
export function extractSchemaFaqItems(value: unknown): SchemaFaqItem[] {
  const faqPage = asRecord(value)
  const mainEntity = Array.isArray(faqPage?.mainEntity)
    ? faqPage.mainEntity
    : []

  return mainEntity.flatMap((rawQuestion) => {
    const question = asRecord(rawQuestion)
    const questionText = cleanString(question?.name)
    const answerText = readAcceptedAnswerText(question?.acceptedAnswer)
    if (!questionText || !answerText) return []
    return [{ question: questionText, answer: answerText }]
  })
}

/**
 * Builds a schema.org FAQPage object, or null when no renderable items
 * survive sanitization (callers skip the script tag in that case — parity
 * with the conditional `<FAQPageJsonLd>` render).
 */
export function buildFaqPageJsonLd(
  items: Array<{ question: string; answer: string }>
): Record<string, unknown> | null {
  const sanitized = sanitizeSchemaFaqItems(items)
  if (sanitized.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: sanitized.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}

/**
 * CreativeWork parts for a hub CollectionPage, one per assigned report.
 * Returns [] when the hub has no stable publish/update date — parity with the
 * previous next-seo adapter, which omitted `hasPart` entries in that case.
 */
export function buildCollectionPageParts(input: {
  title: string
  reports: Array<{ title: string; imageUrl?: string | null }>
  publishedAt?: string | null
  updatedAt?: string | null
  author?: string
}): SchemaCollectionPagePart[] {
  const datePublished =
    cleanString(input.publishedAt) ?? cleanString(input.updatedAt)
  if (!datePublished) return []

  return input.reports.flatMap((report) => {
    const name = cleanString(report.title)
    if (!name) return []

    return [
      {
        author: input.author ?? 'Dupe',
        about: input.title,
        name,
        datePublished,
        ...(report.imageUrl ? { image: report.imageUrl } : {}),
      },
    ]
  })
}

/** Builds a schema.org CollectionPage object for a hub page. */
export function buildCollectionPageJsonLd(input: {
  name: string
  description: string
  url: string
  parts: SchemaCollectionPagePart[]
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: input.name,
    description: input.description,
    url: input.url,
    hasPart: input.parts.map((part) => ({
      ...part,
      '@type': 'CreativeWork',
    })),
  }
}
